/**
 * TanStack Query client configuration and setup
 */

import { QueryClient, type QueryClientConfig } from '@tanstack/react-query'
import type { ApiClient } from '../client/api-client'

const { VITE_NODE_ENV: NODE_ENV } = import.meta.env;

/**
 * Default query client configuration
 */
const defaultQueryConfig: QueryClientConfig = {
  defaultOptions: {
    queries: {
      // Cache data for 5 minutes by default
      staleTime: 5 * 60 * 1000,
      // Keep data in cache for 10 minutes
      gcTime: 10 * 60 * 1000,
      // Retry failed requests 3 times
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (client errors)
        if (error && typeof error === 'object' && 'status' in error) {
          const status = (error as any).status
          if (typeof status === 'number' && status >= 400 && status < 500) {
            return false
          }
        }
        return failureCount < 3
      },
      // Retry with exponential backoff
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Refetch on window focus in production
      refetchOnWindowFocus: NODE_ENV === 'production',
      // Don't refetch on reconnect by default
      refetchOnReconnect: false,
      // Network mode
      networkMode: 'online',
    },
    mutations: {
      // Retry mutations once
      retry: 1,
      // Retry delay for mutations
      retryDelay: 1000,
      // Network mode for mutations
      networkMode: 'online',
    },
  },
}

/**
 * Create a configured QueryClient instance
 */
export function createQueryClient(
  apiClient?: ApiClient,
  config: QueryClientConfig = {}
): QueryClient {
  const mergedConfig: QueryClientConfig = {
    ...defaultQueryConfig,
    ...config,
    defaultOptions: {
      ...defaultQueryConfig.defaultOptions,
      ...config.defaultOptions,
      queries: {
        ...defaultQueryConfig.defaultOptions?.queries,
        ...config.defaultOptions?.queries,
        // Add global error handling
        throwOnError: error => {
          // Log errors in development
          if (NODE_ENV === 'development') {
            console.error('Query error:', error)
          }

          // Always throw errors so they can be handled by the UI
          return true
        },
      },
      mutations: {
        ...defaultQueryConfig.defaultOptions?.mutations,
        ...config.defaultOptions?.mutations,
        // Add global mutation error handling
        throwOnError: error => {
          // Log mutation errors in development
          if (NODE_ENV === 'development') {
            console.error('Mutation error:', error)
          }

          // Always throw mutation errors so they can be handled by the UI
          return true
        },
      },
    },
  }

  const queryClient = new QueryClient(mergedConfig)

  // Add global error handler
  queryClient.setMutationDefaults(['default'], {
    mutationFn: async (variables: any) => {
      if (!apiClient) {
        throw new Error('API client not configured')
      }
      return variables
    },
  })

  return queryClient
}

/**
 * Default query client instance
 */
export const queryClient = createQueryClient()

/**
 * Query key factory for consistent key generation
 */
export const queryKeys = {
  // Base keys
  all: ['api'] as const,

  // Resource-based keys
  users: () => [...queryKeys.all, 'users'] as const,
  user: (id: string) => [...queryKeys.users(), id] as const,
  userProfile: (id: string) => [...queryKeys.user(id), 'profile'] as const,

  posts: () => [...queryKeys.all, 'posts'] as const,
  post: (id: string) => [...queryKeys.posts(), id] as const,
  postComments: (id: string) => [...queryKeys.post(id), 'comments'] as const,

  // Search and filters
  search: (query: string) => [...queryKeys.all, 'search', query] as const,
  filtered: (resource: string, filters: Record<string, any>) =>
    [...queryKeys.all, resource, 'filtered', filters] as const,

  // Paginated queries
  paginated: (resource: string, page: number, limit: number) =>
    [...queryKeys.all, resource, 'paginated', { page, limit }] as const,

  // Infinite queries
  infinite: (resource: string, filters?: Record<string, any>) =>
    [...queryKeys.all, resource, 'infinite', filters] as const,

  // Custom query key generator
  custom: (key: string, ...params: any[]) => [...queryKeys.all, key, ...params] as const,
}

/**
 * Query options factory for common patterns
 */
export const queryOptions = {
  // Standard fetch with caching
  standard: <T>(key: readonly unknown[], fetcher: () => Promise<T>) => ({
    queryKey: key,
    queryFn: fetcher,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  }),

  // Real-time data (short cache)
  realtime: <T>(key: readonly unknown[], fetcher: () => Promise<T>) => ({
    queryKey: key,
    queryFn: fetcher,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 30 * 1000, // Refetch every 30 seconds
  }),

  // Static data (long cache)
  static: <T>(key: readonly unknown[], fetcher: () => Promise<T>) => ({
    queryKey: key,
    queryFn: fetcher,
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 24 * 60 * 60 * 1000, // 24 hours
  }),

  // User-specific data
  user: <T>(key: readonly unknown[], fetcher: () => Promise<T>) => ({
    queryKey: key,
    queryFn: fetcher,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  }),

  // Background sync
  background: <T>(key: readonly unknown[], fetcher: () => Promise<T>) => ({
    queryKey: key,
    queryFn: fetcher,
    staleTime: 0, // Always stale
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  }),
}

/**
 * Mutation options factory
 */
export const mutationOptions = {
  // Standard mutation
  standard: <TData, TVariables>(mutationFn: (variables: TVariables) => Promise<TData>) => ({
    mutationFn,
    retry: 1,
    retryDelay: 1000,
  }),

  // Optimistic mutation
  optimistic: <TData, TVariables>(
    mutationFn: (variables: TVariables) => Promise<TData>,
    optimisticUpdate?: (variables: TVariables) => TData
  ) => ({
    mutationFn,
    retry: 1,
    retryDelay: 1000,
    ...(optimisticUpdate && {
      onMutate: optimisticUpdate,
    }),
  }),

  // Critical mutation (no retry on client errors)
  critical: <TData, TVariables>(mutationFn: (variables: TVariables) => Promise<TData>) => ({
    mutationFn,
    retry: (failureCount: number, error: any) => {
      if (error?.status >= 400 && error?.status < 500) {
        return false
      }
      return failureCount < 2
    },
    retryDelay: 2000,
  }),
}
